<template>
  <button @click="handleClick" :class="buttonClasses" class="button-size-slim">
    <slot />
  </button>
</template>

<script>
export default {
  name: 'Button',
  props: {
    bType: {
      type: String,
      default: 'primary',
    },
    bWidth: {
      type: String,
      default: 'default',
    },
    bColor: {
      type: String,
      default: 'primary',
    },
    bTextColor: {
      type: String,
      default: 'light',
    },
    bMargin: {
      type: String,
      default: '',
    },
    bDisabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    buttonClasses() {
      let classes = 'button focus:outline-none relative';
      classes += ' button-type-' + this.bType;
      classes += ' button-width-' + this.bWidth;
      classes += ' button-text-' + this.textColor;
      classes += ' button-margin-' + this.bMargin;

      if (this.bDisabled) {
        classes += ' disabled';
      }
      return classes;
    },
    textColor() {
      if (this.bTextColor !== 'light') {
        return this.bTextColor;
      }
      if (this.bType === 'secondary' || this.bType === 'ghost') {
        return 'dark';
      }
      return this.bTextColor;
    },
  },
  methods: {
    handleClick() {
      if (!this.bDisabled) {
        this.$emit('button-click');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
button.button--transparent,
.button-type-ghost {
  background-color: transparent;
  color: black;
  font-weight: 700;

  &:hover,
  &:focus {
    background-color: transparent;
    outline: none;
  }
}

.button {
  cursor: pointer;
  display: inline-block;
  white-space: nowrap;
  font-style: normal;
  font-weight: 400;
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  border-radius: 4px;
  line-height: 20px;
  color: #fff;
  min-width: 32px;
  padding: 8px 16px;
  margin: 0;
  transition: all 0.25s ease-in-out;

  &.mt-3 {
    margin-top: 0.75rem;
  }
  &.mb-3 {
    margin-bottom: 0.75rem;
  }

  text-transform: lowercase;
  &::first-letter {
    text-transform: capitalize;
  }

  &.no-transform {
    text-transform: none;
  }

  // Types
  &.button-type-primary {
    background-color: #7c5bc2;
    font-weight: bold;
    border: 1px solid #7c5bc2;
    &:hover {
      background-color: #5b37a8;
    }
    &:disabled {
      color: #8c9196;
      background-color: #f1f1f1;
    }
  }
  &.button-type-secondary {
    background-color: #fff;
    color: #707070;
    border: 1px solid #dadee6;

    &:hover {
      background-color: #fff;
      color: #5e6471;
      border-color: #aeb5c1;
    }
  }
  &.button-type-light {
    background-color: #ffffff;
    color: #202223 !important;
    border: 1px solid #b9bfc6;
    &:hover {
      background-color: #f6f6f7;
      border: 1px solid #abb0b6;
    }
    &:focus {
      background-color: #fff;
      color: #6d7175 !important;
    }
    &:disabled {
      border: 1px solid #d2d5d8;
      background-color: #fafbfb;
      color: #8c9196 !important;
    }
  }
  &.button-type-dark {
    background-color: #3c4042;
    border: 1px solid #b9bfc6;
    &:hover {
      background-color: #54575b;
      border: 1px solid #abb0b6;
    }
    &:focus {
      background-color: #3c4042;
      color: #b9bfc6 !important;
    }
    &:disabled {
      border: 1px solid #b9bfc6;
      background-color: #3c4042;
      color: #8c9196 !important;
    }
  }
  &.button-type-ghost,
  &.button-type-transparent {
    color: #202223;
    background-color: transparent;
    border: 1px solid #abb0b6;

    &:hover {
      background: #f6f6f7;
      border: 1px solid #a0a0a0;
    }
  }
  &.button-type-text {
    background-color: transparent;
    border: none;
    padding: 4px 8px;
    &:disabled {
      color: #8c9196 !important;
    }
  }

  &.button-type-error {
    background-color: #d82c0d;
    font-weight: bold;
    &:hover {
      background-color: #cd290c;
    }
    &:disabled {
      background-color: #f1f1f1;
      color: #8c9196 !important;
    }
  }

  // Text Colors
  &.button-text-primary {
    color: #2c6ecb !important;

    &:hover {
      color: #1f5199 !important;
    }
  }
  &.button-text-mono {
    color: #202223 !important;
  }
  &.button-text-light:not(:disabled) {
    color: #fff;
    &:hover {
      color: #f5f5f5;
    }
  }
  &.button-text-dark {
    color: #727a88;
    &:hover {
      color: #5e6471;
    }
  }
  &.button-text-red {
    color: #d82c0d;
    &:hover {
      color: #cd290c;
    }
  }

  // Utilities
  &.button-width-expanded {
    display: block;
    text-align: center;
    width: 100%;
    margin-left: 0;
    margin-right: 0;
  }
  &.is-disabled {
    cursor: not-allowed;
    opacity: 0.5;

    &:hover {
      transform: none;
    }
  }
  &.button--input {
    margin-right: -12px;
    background-color: transparent;
    color: #4d48e8;

    &:hover {
      background-color: #f5f6fa;
    }
  }
  &.button--close {
    border: 2px solid #e3e6ec;
    background-color: #fff;
    color: #000;

    &:hover {
      background-color: #fafafa;
      color: #595f6c;
    }
  }

  // Margins
  &.button-margin-top {
    margin-top: 12px;
  }
  &.button-margin-bottom {
    margin-bottom: 12px;
  }
  &.button-margin-left {
    margin-left: 12px;
  }
  &.button-margin-right {
    margin-right: 12px;
  }
  &.button-margin-x {
    margin-top: 12px;
    margin-bottom: 12px;
  }
  &.button-margin-y {
    margin-left: 12px;
    margin-right: 12px;
  }
  &.button-margin-all {
    margin: 12px;
  }
  &.button-margin-none {
    margin: 0;
  }
}
</style>
